<template>
  <StepCard
    step-id="set_up_project"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('save_projects')"
    :save-hint
    @save="save"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <dl
        class="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2"
        data-testid="project-summary"
      >
        <div
          v-for="field in summaryFields"
          :key="field.label"
        >
          <dt class="text-xs font-medium uppercase text-gray-600">{{ field.label }}</dt>
          <dd
            class="mt-1 truncate text-sm"
            :class="field.muted ? 'text-gray-500' : 'font-medium text-gray-900'"
          >
            {{ field.value }}
          </dd>
        </div>
      </dl>
    </template>

    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-start">
      <form
        class="grid grid-cols-1 gap-5"
        novalidate
        @submit="save"
      >
        <ZoneTextInput
          v-model="name"
          :label="t('project_name')"
          :placeholder="t('project_placeholder')"
          :invalid="!!errors.name"
          required
          autocomplete="off"
          data-testid="project-name"
        >
          <template #notice>
            <p
              v-if="errors.name"
              class="mt-1 text-xs text-red-600"
              role="alert"
              data-testid="project-name-error"
            >
              {{ errors.name }}
            </p>
          </template>
        </ZoneTextInput>
        <ZoneSelect
          v-model="selectedClientId"
          :label="t('client')"
          :clearable="false"
          :helper-text="t('client_helper')"
          :options="clientOptions"
          data-testid="project-client"
          @update:model-value="validateField('newClientName')"
        />
        <ZoneTextInput
          v-if="clientId === NEW_CLIENT"
          v-model="newClientName"
          :label="t('new_client_name')"
          :invalid="!!errors.newClientName"
          required
          autocomplete="off"
          data-testid="project-new-client"
        >
          <template #notice>
            <p
              v-if="errors.newClientName"
              class="mt-1 text-xs text-red-600"
              role="alert"
              data-testid="project-new-client-error"
            >
              {{ errors.newClientName }}
            </p>
          </template>
        </ZoneTextInput>
        <div
          v-if="!data.hide_budget_card"
          class="rounded-lg border border-gray-200 bg-white p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex min-w-0 items-center gap-2">
              <ZoneIcon
                icon="bolt"
                class="text-gray-500"
                aria-hidden="true"
              />
              <div class="flex min-w-0 flex-wrap-reverse items-center gap-x-2 gap-y-1">
                <span class="whitespace-nowrap text-sm font-medium text-gray-900">{{ t('weekly_budget') }}</span>
                <ZonePill color="primary">{{ t('recommended') }}</ZonePill>
              </div>
            </div>
            <ZoneToggle
              v-model="budgetEnabled"
              size="small"
              :aria-label="t('weekly_budget')"
              data-testid="project-budget"
              @update:model-value="validateField('budgetHours')"
            />
          </div>
          <p class="mt-2 text-sm text-gray-500">{{ t('budget_description') }}</p>
          <div
            v-if="budgetEnabled"
            class="mt-3"
          >
            <div class="flex items-center gap-3">
              <div class="w-24">
                <ZoneTextInput
                  v-model="budgetHours"
                  inputmode="decimal"
                  placeholder="40"
                  :aria-label="t('budget_hours')"
                  :invalid="!!errors.budgetHours"
                  data-testid="project-budget-hours"
                />
              </div>
              <span class="whitespace-nowrap text-sm text-gray-500">{{ t('budget_unit') }}</span>
            </div>
            <p
              v-if="errors.budgetHours"
              class="mt-1 text-xs text-red-600"
              role="alert"
              data-testid="project-budget-hours-error"
            >
              {{ errors.budgetHours }}
            </p>
          </div>
        </div>
      </form>

      <div
        class="@container"
        aria-hidden="true"
      >
        <p class="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
          <ZoneIcon
            icon="timer"
            class="text-gray-600"
          />
          {{ t('preview') }}
          <span class="font-regular text-gray-400">·</span>
          <span class="font-regular text-gray-600">{{ t('preview_subtitle') }}</span>
        </p>
        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div class="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-3.5 py-2.5">
            <span class="size-2.5 rounded-full bg-red-500" />
            <span class="size-2.5 rounded-full bg-orange-400" />
            <span class="size-2.5 rounded-full bg-green-400" />
          </div>
          <div class="flex flex-col items-center gap-3 px-5 pb-4 pt-5">
            <div class="rounded-md bg-primary-700 px-8 py-2 font-mono text-lg font-semibold tabular-nums text-white">
              {{ clock }}
            </div>
            <div class="w-full text-center">
              <p
                class="truncate text-sm font-semibold"
                :class="name.trim() ? 'text-gray-900' : 'text-gray-400'"
                data-testid="project-preview-name"
              >
                {{ name.trim() || t('preview_placeholder') }}
              </p>
              <p
                v-if="clientName"
                class="mt-0.5 truncate text-xs text-gray-500"
              >
                {{ clientName }}
              </p>
            </div>
            <span class="flex size-11 items-center justify-center rounded-full border border-gray-200 bg-white">
              <span class="size-3.5 rounded-sm bg-primary-700" />
            </span>
            <div class="mt-1 grid w-full grid-cols-1 gap-4 @xs:grid-cols-2 text-xs text-gray-600">
              <div
                v-for="bar in previewBars"
                :key="bar.label"
                data-testid="project-preview-bar"
              >
                <p class="truncate">
                  {{ bar.label }} <span class="font-semibold text-gray-900">{{ bar.time }}</span> / {{ bar.total }}
                </p>
                <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div
                    class="h-full rounded-full bg-primary-500"
                    :style="{ width: bar.width }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <p class="mt-3 text-xs text-gray-500">{{ t('preview_footer') }}</p>
      </div>
    </div>
  </StepCard>
</template>

<script setup>
import { useForm } from 'vee-validate';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { BUDGET_TYPE_HOURS } from '~/app_constants';
import { ClientsResource, ProjectsResource } from '~/resources';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZonePill from 'zone/components/base/Pill.vue';
import ZoneSelect from 'zone/components/base/Select.vue';
import ZoneTextInput from 'zone/components/base/TextInput.vue';
import ZoneToggle from 'zone/components/base/Toggle.vue';
import { chain, max, numeric, positive, required } from '~/zone_inertia/validations';

const NEW_CLIENT = 'new';
const NO_CLIENT = 'none';
const DEFAULT_BUDGET_HOURS = 40;
const PREVIEW_PROJECT_SHARE = 0.263;
const PROJECT_NAME_MAX_LENGTH = 256;
const CLIENT_NAME_MAX_LENGTH = 100;
const PREVIEW_START_SECONDS = 5275;

const { data, completed, organization, currentUserId } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
  currentUserId: { type: Number, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, serverMessages, finishStep, run } = useStepSave(emit);

const budgetEnabled = ref(!!data.project?.budget || (!completed && !data.hide_budget_card));

const { defineField, errors, meta, handleSubmit, validateField } = useForm({
  initialValues: {
    name: data.project?.name ?? '',
    clientId: data.project?.client_id ?? null,
    newClientName: '',
    budgetHours: String(data.project?.budget?.hours ?? DEFAULT_BUDGET_HOURS),
  },
  validationSchema: {
    name: chain([required(), max(PROJECT_NAME_MAX_LENGTH)]),
    newClientName: (value, { form }) => form.clientId !== NEW_CLIENT || chain([required(), max(CLIENT_NAME_MAX_LENGTH)])(value),
    budgetHours: (value) => !budgetEnabled.value || chain([required(), numeric(), positive()])(value),
  },
});
const [name] = defineField('name');
const [clientId] = defineField('clientId');
const [newClientName] = defineField('newClientName');
const [budgetHours] = defineField('budgetHours');
const createdProjectId = ref(null);
const createdClients = ref([]);
const elapsed = ref(0);
let timer = null;

const projectId = computed(() => data.project?.id ?? createdProjectId.value);
const saveHint = computed(() => (meta.value.valid ? null : t('fix_errors')));
const clients = computed(() => [...data.clients, ...createdClients.value]);
const clientOptions = computed(() => [
  { id: NO_CLIENT, name: t('no_client') },
  ...clients.value,
  { id: NEW_CLIENT, name: t('new_client') },
]);
const selectedClientId = computed({
  get: () => clientId.value ?? NO_CLIENT,
  set: (id) => { clientId.value = id === NO_CLIENT ? null : id; },
});
const clientName = computed(() => {
  if (clientId.value === NEW_CLIENT) {return newClientName.value.trim();}

  return clients.value.find((client) => client.id === clientId.value)?.name;
});

const pad = (value) => String(value).padStart(2, '0');
const clock = computed(() => {
  const seconds = PREVIEW_START_SECONDS + elapsed.value;

  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
});
const formatMinutes = (minutes) => `${Math.floor(minutes / 60)}:${pad(minutes % 60)}`;
const budgetTotalHours = computed(() => {
  const hours = parseFloat(budgetHours.value);

  return Number.isFinite(hours) && hours > 0 ? hours : DEFAULT_BUDGET_HOURS;
});
const previewBars = computed(() => [
  ...(budgetEnabled.value ? [{ label: t('preview_project'), hours: budgetTotalHours.value, minutes: Math.round(budgetTotalHours.value * 60 * PREVIEW_PROJECT_SHARE) }] : []),
  { label: t('preview_weekly'), hours: DEFAULT_BUDGET_HOURS, minutes: 507 },
].map(({ label, hours, minutes }) => ({
  label,
  time: formatMinutes(minutes + Math.floor(elapsed.value / 60)),
  total: formatMinutes(Math.round(hours * 60)),
  width: `${Math.min(100, ((minutes * 60 + elapsed.value) / (hours * 3600)) * 100)}%`,
})));

const summaryFields = computed(() => {
  const project = data.project ?? {};
  const client = data.clients.find(({ id }) => id === project.client_id);

  return [
    { label: t('project_name'), value: project.name },
    { label: t('client'), value: client?.name ?? t('no_client'), muted: !client },
    { label: t('weekly_budget'), value: project.budget ? t('budget_value', { hours: project.budget.hours }) : t('off'), muted: !project.budget },
  ];
});

const save = handleSubmit(() => run(async () => {
  if (clientId.value === NEW_CLIENT) {
    const response = await ClientsResource.create({ id: organization.id }, { client: { name: newClientName.value.trim() } });

    if (!response.success) {return serverMessages(response);}

    createdClients.value = [...createdClients.value, response.client];
    clientId.value = response.client.id;
  }

  const project = { name: name.value.trim(), client_id: clientId.value };

  if (projectId.value) {
    const response = await ProjectsResource.update({ id: projectId.value }, { project });

    if (!response.success) {return serverMessages(response);}
  } else {
    const response = await ProjectsResource.create(
      { id: organization.id },
      { skip_existing_project: true, projects: [{ ...project, members: [{ user_id: currentUserId, role: 'manager', selected: true }] }] },
    );

    if (!response.success) {return serverMessages(response);}
    if (!response.projects.length) {return [t('project_exists')];}

    createdProjectId.value = response.projects[0].id;
  }

  const hours = parseFloat(budgetHours.value);

  if (!data.hide_budget_card && budgetEnabled.value && hours !== Number(data.project?.budget?.hours)) {
    const response = await ProjectsResource.updateBudget(
      { id: projectId.value },
      { project: { budget_attributes: { ...data.project?.budget, type: BUDGET_TYPE_HOURS, rate: 0, hours, recurrence: 'weekly' } } },
    );

    if (response.action !== 'reload') {return [t('budget_failed')];}
  } else if (!budgetEnabled.value && data.project?.budget) {
    const response = await ProjectsResource.removeBudget({ id: projectId.value });

    if (response.action !== 'reload') {return [t('budget_remove_failed')];}
  }

  return finishStep(organization.id, 'set_up_project');
}));

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {return;}

  timer = setInterval(() => { elapsed.value += 1; }, 1000);
});

onBeforeUnmount(() => clearInterval(timer));
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Watch the preview update as you go. This is what your team — and your client — will see.",
    "project_name": "Project name",
    "project_placeholder": "e.g. Acme — website refresh",
    "client": "Client",
    "no_client": "No client",
    "new_client": "Add a new client",
    "new_client_name": "New client name",
    "client_helper": "Optional — used for reporting and invoicing.",
    "weekly_budget": "Weekly project budget",
    "recommended": "Recommended",
    "budget_description": "We suggest 40 total hours per week. You can increase this if your team is larger.",
    "budget_value": "{hours} h / week",
    "budget_hours": "Weekly budget hours",
    "budget_unit": "hours / week",
    "off": "Off",
    "preview": "Preview",
    "preview_subtitle": "What your team sees",
    "preview_placeholder": "Your project name",
    "preview_project": "Project:",
    "preview_weekly": "Weekly:",
    "preview_footer": "Your team tracks time to this project. You can create as many projects as you'd like.",
    "project_exists": "A project with this name already exists. Check your archived projects too.",
    "budget_failed": "We could not set the weekly budget. Try again.",
    "budget_remove_failed": "We could not remove the weekly budget. Try again.",
    "save_projects": "Save project",
    "edit": "Edit project"
  }
}
</i18n>
